export interface WeaponLevelDisplayProps {
  currentLevel: number;
  currentPercent: number;
  requiredLevel: number;
  /** True once the level came from a real crafted weapon rather than a default. */
  fromAccount?: boolean;
}

/**
 * The bottom-left level readout.
 *
 * Read-only, like the game. The level comes from the signed-in account's actual
 * crafted weapon; when shaping something new it is 0, which is correct — a
 * freshly shaped weapon starts at the bottom, so the required level and the
 * remaining levels are exactly the grind ahead of you.
 */
export function WeaponLevelDisplay({
  currentLevel,
  currentPercent,
  requiredLevel,
  fromAccount,
}: WeaponLevelDisplayProps) {
  const reached = currentLevel >= requiredLevel;

  return (
    <div className="max-w-sm">
      <div className="flex items-baseline gap-3">
        <span className="text-3xl leading-none font-semibold tabular-nums sm:text-4xl">
          {currentLevel}
        </span>
        <span className="label-caps text-sm text-ink-2">Weapon Level</span>
      </div>

      <div className="mt-2 h-1.5 w-full bg-white/12" role="presentation">
        <div
          className="h-full transition-[width] duration-200 ease-(--ease-crisp)"
          style={{ width: `${currentPercent}%`, background: "var(--color-level)" }}
        />
      </div>

      <div className="mt-1.5 flex items-baseline justify-between text-xs text-ink-2">
        <span>Lv. {currentLevel}</span>
        <span className={reached ? "text-ok" : undefined}>
          {reached ? "Target reached" : `Target Lv. ${requiredLevel}`}
        </span>
      </div>

      {!fromAccount && (
        <p className="mt-2 text-xs text-ink-3">
          Planning a new shape from level 0. Sign in with Bungie to plan against a weapon you
          already own.
        </p>
      )}
    </div>
  );
}
